import * as React from 'react';

import { Rsvp } from '../../reducers/root';
import BaseForm from './baseForm';
import YesNoRadioButtons from './yesNoRadioButtons';
import ErrorableYesNoRadioButtons from './errorableYesNoRadioButtons';
import InputErrorMessage from './inputErrorMessage';

interface RsvpFormProps {
  onSubmit: (rsvp: Rsvp) => void;
}

interface RsvpFormState {
  name: string;
  attending: boolean | undefined;
  // no details
  noNote: string;
  // yes details
  phoneNumber: string;
  drinks: boolean | undefined;
  guestName: string;
  allergies: string;
  address: string;
  yesNote: string;
  showErrors: boolean;
}

const defaultState: RsvpFormState = {
  name: '',
  attending: undefined,
  // no details
  noNote: '',
  // yes details
  phoneNumber: '',
  drinks: undefined,
  guestName: '',
  allergies: '',
  address: '',
  yesNote: '',
  showErrors: false,
};

export default class RsvpForm extends BaseForm<RsvpFormProps, RsvpFormState> {
  state = { ...defaultState };

  onYesSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const formattedPhoneNumber = this.phoneNumberIsValid();

    if (!(this.state.name && this.state.drinks !== undefined) || !formattedPhoneNumber) {
      this.setState({ showErrors: true });
      return;
    }
    this.props.onSubmit({
      attending: true,
      name: this.state.name,
      phoneNumber: formattedPhoneNumber,
      drinks: this.state.drinks,
      guestName: this.state.guestName || '-',
      allergies: this.state.allergies || '-',
      address: this.state.address ? this.state.address.replace(/\r\n|\r|\n/g, ' //// ') : '-',
      note: this.state.yesNote ? this.state.yesNote.replace(/\r\n|\r|\n/g, ' //// ') : '-',
    });
    this.setState({ ...defaultState });
  };

  onNoSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!this.state.name) {
      this.setState({ showErrors: true });
      return;
    }
    this.props.onSubmit({
      attending: false,
      name: this.state.name,
      note: this.state.noNote ? this.state.noNote.replace(/\r\n|\r|\n/g, ' //// ') : '-',
    });
    this.setState({ ...defaultState });
  };

  phoneNumberIsValid(): string | undefined {
    if (this.state.phoneNumber) {
      if (/\d{3}-\d{3}-\d{4}/.test(this.state.phoneNumber)) {
        return this.state.phoneNumber;
      }

      if (/\d{10}/.test(this.state.phoneNumber)) {
        return `${this.state.phoneNumber.slice(0, 3)}-${this.state.phoneNumber.slice(
          3,
          6,
        )}-${this.state.phoneNumber.slice(6)}`;
      }

      if (/\(\d{3}\) \d{3}-\d{4}/.test(this.state.phoneNumber)) {
        return `${this.state.phoneNumber.slice(1, 4)}-${this.state.phoneNumber.slice(6)}`;
      }
    }

    return;
  }

  details(): JSX.Element | null {
    if (this.state.attending) {
      return this.yesDetails();
    }
    if (this.state.attending === false) {
      return this.noDetails();
    }
    return null;
  }

  yesDetails(): JSX.Element {
    return (
      <div>
        <div className="field control">
          <label className="label">What's your phone number?</label>
          <input
            className="input"
            type="tel"
            placeholder="xxx-xxx-xxxx"
            name="phoneNumber"
            value={this.state.phoneNumber}
            onChange={this.onValueChange}
          />
          <InputErrorMessage
            showError={!this.phoneNumberIsValid() && this.state.showErrors}
            message="Please enter your phone number in the xxx-xxx-xxxx format."
          />
        </div>

        <ErrorableYesNoRadioButtons
          question="Will you be coming to have welcome drinks on Friday evening?"
          name="drinks"
          isYesChecked={this.state.drinks === true}
          isNoChecked={this.state.drinks === false}
          onChange={this.onBooleanChange}
          showError={this.state.drinks === undefined && this.state.showErrors}
          errorMessage="Please let us know if you will come to the welcome drinks."
        />

        <div className="field control">
          <label className="label">If you're bringing a guest, what is their name?</label>
          <input
            className="input"
            type="text"
            placeholder="Name of guest"
            name="guestName"
            value={this.state.guestName}
            onChange={this.onValueChange}
          />
        </div>

        <div className="field control">
          <label className="label">
            If you have any dietary restrictions or food allergies, please let us know about them:
          </label>
          <input
            className="input"
            type="text"
            placeholder="Dietary restrictions (optional)"
            name="allergies"
            value={this.state.allergies}
            onChange={this.onValueChange}
          />
        </div>

        <div className="field control">
          <label className="label">
            If your address will be changing in the near future, please let us know your new one:
          </label>
          <textarea
            className="textarea"
            name="address"
            value={this.state.address}
            onChange={this.onValueChange}
            placeholder="New address details (optional)"
          />
        </div>

        <div className="field control">
          <label className="label">If you'd like, leave us a note:</label>
          <textarea
            className="textarea"
            name="yesNote"
            value={this.state.yesNote}
            onChange={this.onValueChange}
            placeholder="Note (optional)"
          />
        </div>

        <div className="field control">
          <button className="button is-dark" onClick={this.onYesSubmit}>
            I'm excited to come!
          </button>
        </div>
      </div>
    );
  }

  noDetails(): JSX.Element {
    return (
      <div>
        <div className="field control">
          <label className="label">If you'd like, leave us a note:</label>
          <textarea
            className="textarea"
            name="noNote"
            value={this.state.noNote}
            onChange={this.onValueChange}
            placeholder="Note (optional)"
          />
        </div>

        <div className="field control">
          <button className="button is-dark" onClick={this.onNoSubmit}>
            Sorry, I can't make it
          </button>
        </div>
      </div>
    );
  }

  render(): JSX.Element {
    return (
      <form>
        <div className="field control">
          <label className="label">Name</label>
          <input
            className="input"
            type="text"
            placeholder="Name"
            name="name"
            value={this.state.name}
            onChange={this.onValueChange}
            autoFocus
          />
          <InputErrorMessage
            showError={!this.state.name && this.state.showErrors}
            message="Please tell us your name."
          />
        </div>

        <YesNoRadioButtons
          question="Will you be attending the wedding?"
          name="attending"
          isYesChecked={this.state.attending === true}
          isNoChecked={this.state.attending === false}
          onChange={this.onBooleanChange}
        />
        {this.details()}
      </form>
    );
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/forms/rsvpForm.tsx